// Updated DocumentsCloudStorage component with fixes

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import InputField from '../../components/common/InputField/InputField';
import styles from './DocumentsCloudStorage.module.scss';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bankDocumentService } from '../../services/bankDocumentService';
import { API_BASE_URL, BACKEND_BASE_URL } from '../../utils/constants';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface CloudFile {
  id: number;
  file_url: string;
  file_size: number;
  file_type: string;
  storage_path: string;
  created_at: string;
  folder_id?: number;
  name?: string;
}

interface Folder {
  id: number;
  name: string;
  parent_id?: number;
  created_at: string;
  path: string;
}

interface BreadcrumbItem {
  id: number | null;
  name: string;
}

const DocumentsCloudStorage: React.FC = () => {
  const { auth } = useAuth();
  const [documents, setDocuments] = useState<CloudFile[] | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [allFolders, setAllFolders] = useState<Folder[]>([]); // Store all folders
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<CloudFile | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<Folder | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [folderName, setFolderName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState<CloudFile[]>([]);
  const [filteredFolders, setFilteredFolders] = useState<Folder[]>([]);
  const [storageStats, setStorageStats] = useState<{
    allocationGB: number;
    usedSpaceGB: number;
    freeSpaceGB: number | null;
  } | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([{ id: null, name: 'Root' }]);

  const storageData = storageStats ? [
    { name: 'Used Space', value: storageStats.usedSpaceGB },
    { name: 'Free Space', value: storageStats.freeSpaceGB || 0 }
  ] : [];

  useEffect(() => {
    fetchDocuments();
    fetchFolders();
  }, [currentFolderId]);

  // Filter documents and folders based on search term and current folder
  useEffect(() => {
    if (!documents) {
      setFilteredDocuments([]);
      return;
    }
    
    const filteredDocs = documents.filter(doc => {
      if (!doc || !doc.file_url) return false;
      const matchesFolder = doc.folder_id === currentFolderId;
      const matchesSearch = searchTerm === '' || 
        getFileNameFromUrl(doc.file_url).toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFolder && matchesSearch;
    });
    
    setFilteredDocuments(filteredDocs);
  }, [searchTerm, documents, currentFolderId]);

  // Filter folders for current level
  useEffect(() => {
    if (!allFolders) {
      setFilteredFolders([]);
      return;
    }
    
    const currentLevelFolders = allFolders.filter(folder => {
      if (!folder || !folder.name) return false;
      const matchesParent = folder.parent_id === currentFolderId;
      const matchesSearch = searchTerm === '' || 
        folder.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesParent && matchesSearch;
    });
    
    setFilteredFolders(currentLevelFolders);
  }, [searchTerm, allFolders, currentFolderId]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }
  
    try {
      const response = await bankDocumentService.searchDocuments(auth?.user?.id || "0", searchTerm);
      setFilteredDocuments(response.documents || []);
      setFilteredFolders(response.folders || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search documents and folders');
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      let response;
      if (auth?.user?.role === 'admin') {
        response = await bankDocumentService.getDocumentsCloudAdmin(auth?.user?.id || "0", currentFolderId);
      } else {
        response = await bankDocumentService.getDocumentsCloud(auth?.user?.id || "0", currentFolderId);
        setStorageStats({
          allocationGB: response.allocationGB,
          usedSpaceGB: parseFloat(response.usedSpaceGB),
          freeSpaceGB: response.freeSpaceGB ? parseFloat(response.freeSpaceGB) : null
        });
      }
      
      const validDocuments = response.files.filter(doc => {
        return doc && typeof doc === 'object' && doc.id && doc.file_url &&
               doc.file_size !== undefined && doc.storage_path && doc.file_type;
      });

      console.log("validDocuments::::::", validDocuments)
      
      setDocuments(validDocuments);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await bankDocumentService.getFolders(auth?.user?.id || "0");
      setAllFolders(response.folders || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      setAllFolders([]);
    }
  };

  const updateBreadcrumb = async (folderId: number | null) => {
    if (folderId === null) {
      setBreadcrumb([{ id: null, name: 'Root' }]);
      return;
    }

    try {
      const response = await bankDocumentService.getFolderPath(folderId);
      const path = response.path || [];
      const breadcrumbItems = [
        { id: null, name: 'Root' },
        ...path.map((folder: any) => ({ id: folder.id, name: folder.name }))
      ];
      setBreadcrumb(breadcrumbItems);
    } catch (error) {
      console.error('Error fetching folder path:', error);
      // Fallback: try to build breadcrumb from allFolders
      const buildBreadcrumbFromFolders = (folderId: number): BreadcrumbItem[] => {
        const folder = allFolders.find(f => f.id === folderId);
        if (!folder) return [{ id: null, name: 'Root' }];
        
        if (folder.parent_id) {
          const parentBreadcrumb = buildBreadcrumbFromFolders(folder.parent_id);
          return [...parentBreadcrumb, { id: folder.id, name: folder.name }];
        } else {
          return [{ id: null, name: 'Root' }, { id: folder.id, name: folder.name }];
        }
      };
      
      setBreadcrumb(buildBreadcrumbFromFolders(folderId));
    }
  };

  const handleFolderClick = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    updateBreadcrumb(folderId);
  };

  const handleBreadcrumbClick = (folderId: number | null) => {
    setCurrentFolderId(folderId);
    updateBreadcrumb(folderId);
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      await bankDocumentService.createFolder({
        name: folderName,
        parent_id: currentFolderId,
        user_id: auth?.user?.id || "0"
      });
      
      toast.success('Folder created successfully');
      setShowCreateFolderModal(false);
      setFolderName('');
      fetchFolders(); // Refresh all folders
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    
    try {
      await bankDocumentService.deleteFolder(folderToDelete.id);
      toast.success('Folder deleted successfully');
      setShowDeleteFolderModal(false);
      setFolderToDelete(null);
      fetchFolders();
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      setDocumentName(fileName);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentName.trim()) {
      toast.error('Please select a file and enter a document name');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('folder_id', currentFolderId?.toString() || '');
      formData.append('name', documentName);
      
      await bankDocumentService.uploadDocumentCloud(formData);
      
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      setSelectedFile(null);
      setDocumentName('');
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (document: CloudFile) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;
    
    try {
      await bankDocumentService.deleteDocumentCloud(documentToDelete.id);
      setDocuments(prev => prev ? prev.filter(doc => doc.id !== documentToDelete.id) : []);
      toast.success('Document deleted successfully');
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getFileNameFromUrl = (url: string): string => {
    return url.split('/').pop() || '';
  };

  const handleDownload = async (doc: CloudFile) => {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/cloud-download/${doc.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileNameFromUrl(doc.file_url);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getFileIcon = (fileType: string, size = 24) => {
    if (fileType.includes('pdf')) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#dc2626" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">PDF</text>
        </svg>
      );
    }
    
    if (fileType.includes('word') || fileType.includes('document')) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#2563eb" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">DOC</text>
        </svg>
      );
    }
    
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#16a34a" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="12" y="16" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">XLS</text>
        </svg>
      );
    }
    
    if (fileType.includes('image')) {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#7c3aed" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="10" cy="12" r="1" fill="white"/>
          <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Default file icon
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#6b7280" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M14 2v6h6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 13H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M16 17H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M10 9H8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  };

  const getFolderIcon = (size = 24) => {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="#3b82f6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  // Debug info - remove in production
  // console.log('Current folder ID:', currentFolderId);
  // console.log('All folders:', allFolders);
  // console.log('Filtered folders:', filteredFolders);
  // console.log('Documents:', documents);
  // console.log('Filtered documents:', filteredDocuments);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Documents</h2>
          <p>Manage and organize your company documents</p>
        </div>
        <div className={styles.headerRight}>
          <Button variant="secondary" onClick={() => setShowCreateFolderModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              <line x1="12" y1="11" x2="12" y2="17"/>
              <line x1="9" y1="14" x2="15" y2="14"/>
            </svg>
            New Folder
          </Button>
          <Button variant="primary" onClick={() => setShowUploadModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7,10 12,15 17,10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Upload Document
          </Button>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className={styles.breadcrumb}>
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id}>
            <button
              className={styles.breadcrumbItem}
              onClick={() => handleBreadcrumbClick(item.id)}
              disabled={index === breadcrumb.length - 1}
            >
              {item.name}
            </button>
            {index < breadcrumb.length - 1 && (
              <span className={styles.breadcrumbSeparator}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9,18 15,12 9,6"></polyline>
                </svg>
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className={styles.searchSection}>
  <InputField
    name="search"
    placeholder="Search documents and folders..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className={styles.searchInput}
  />
  <Button
    variant="primary"
    size="sm"
    onClick={handleSearch}
    disabled={searchTerm.trim().length < 2}
  >
    Search
  </Button>
</div>

      {storageStats && (
        <div className={styles.storageChart}>
          <h3>Storage Usage</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={storageData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#8884d8"
                label={(entry) => `${entry.name}: ${entry.value} GB`}
              >
                <Cell key="used" fill="#dc2626" />
                <Cell key="free" fill="#16a34a" />
              </Pie>
              <Tooltip formatter={(value: number) => `${value} GB`} />
              <Legend verticalAlign="bottom" />
            </PieChart>
          </ResponsiveContainer>
          <p>Total Allocation: {storageStats.allocationGB} GB</p>
        </div>
      )}

      <div className={styles.documentsList}>
        {loading ? (
          <div className={styles.loading}>
            <p>Loading...</p>
          </div>
        ) : (filteredFolders.length === 0 && filteredDocuments.length === 0) ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📁</div>
            <h3>No items found</h3>
            <p>{searchTerm ? 'No items match your search' : 'This folder is empty'}</p>
            {!searchTerm && (
              <div className={styles.emptyActions}>
                <Button variant="secondary" onClick={() => setShowCreateFolderModal(true)}>
                  Create Folder
                </Button>
                <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                  Upload Document
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.documentsTable}>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Render folders first */}
                {filteredFolders.map((folder) => (
                  <tr key={`folder-${folder.id}`} className={styles.folderRow}>
                    <td className={styles.fileTypeCell}>
                      <span className={styles.fileIcon}>{getFolderIcon()}</span>
                    </td>
                    <td className={styles.nameCell}>
                      <button
                        className={styles.folderName}
                        onClick={() => handleFolderClick(folder.id)}
                      >
                        <strong>{folder.name}</strong>
                      </button>
                    </td>
                    <td className={styles.sizeCell}>—</td>
                    <td className={styles.dateCell}>
                      {formatDate(folder.created_at)}
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.tableActions}>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setFolderToDelete(folder);
                            setShowDeleteFolderModal(true);
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"></polyline>
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                
                {/* Render documents */}
                {filteredDocuments.map((doc) => {
                  if (!doc || !doc.id || !doc.file_url) return null;
                  
                  return (
                    <tr key={`doc-${doc.id}`}>
                      <td className={styles.fileTypeCell}>
                        <span className={styles.fileIcon}>{getFileIcon(doc.file_type)}</span>
                      </td>
                      <td className={styles.nameCell}>
                        <strong>{doc.name || getFileNameFromUrl(doc.file_url)}</strong>
                      </td>
                      <td className={styles.sizeCell}>
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className={styles.dateCell}>
                        {formatDate(doc.created_at)}
                      </td>
                      <td className={styles.actionsCell}>
                        <div className={styles.tableActions}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => window.open(`${BACKEND_BASE_URL}/documents/${doc.storage_path}`, '_blank')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7,10 12,15 17,10"></polyline>
                              <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteClick(doc)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3,6 5,6 21,6"></polyline>
                              <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              // Complete missing code for DocumentsCloudStorage component

                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setSelectedFile(null);
          setDocumentName('');
        }}
        title="Upload Document"
        size="md"
      >
        <div className={styles.modalContent}>
          <div className={styles.fileUpload}>
            <input
              type="file"
              id="fileInput"
              onChange={handleFileSelect}
              className={styles.fileInput}
            />
            <label htmlFor="fileInput" className={styles.fileInputLabel}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7,10 12,15 17,10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {selectedFile ? selectedFile.name : 'Choose file to upload'}
            </label>
          </div>
          
          <InputField
            name="documentName"
            label="Document Name"
            type="text"
            placeholder="Enter document name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            required
          />
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowUploadModal(false);
                setSelectedFile(null);
                setDocumentName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create Folder Modal */}
      <Modal
        isOpen={showCreateFolderModal}
        onClose={() => {
          setShowCreateFolderModal(false);
          setFolderName('');
        }}
        title="Create New Folder"
        size="md"
      >
        <div className={styles.modalContent}>
          <InputField
            name="folderName"
            label="Folder Name"
            type="text"
            placeholder="Enter folder name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            required
          />
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateFolderModal(false);
                setFolderName('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateFolder}
              disabled={!folderName.trim()}
            >
              Create Folder
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Document Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDocumentToDelete(null);
        }}
        title="Delete Document"
        size="md"
      >
        <div className={styles.modalContent}>
          <p>
            Are you sure you want to delete "{documentToDelete?.name || (documentToDelete ? getFileNameFromUrl(documentToDelete.file_url) : '')}"?
            This action cannot be undone.
          </p>
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setDocumentToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Folder Modal */}
      <Modal
        isOpen={showDeleteFolderModal}
        onClose={() => {
          setShowDeleteFolderModal(false);
          setFolderToDelete(null);
        }}
        title="Delete Folder"
        size="sm"
      >
        <div className={styles.modalContent}>
          <p>
            Are you sure you want to delete the folder "{folderToDelete?.name}"?
            This will also delete all contents within the folder. This action cannot be undone.
          </p>
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteFolderModal(false);
                setFolderToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteFolder}
            >
              Delete Folder
            </Button>
          </div>
        </div>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default DocumentsCloudStorage;
